import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Header } from "../components/common/Header";
import { Tabs } from "../components/common/Tabs";
import { ToastRegion, type ToastMessage } from "../components/common/Toast";
import { Footer } from "../components/common/Footer";
import { toolRegistry, type ToolDefinition, type ToastKind } from "./toolRegistry";
import { PROJECT_REPO_URL } from "./projectLinks";

type AppShellProps = {
  tools?: ToolDefinition[];
};

const LAST_TAB_KEY = "serverless-converter:last-tab";
const THEME_KEY = "serverless-converter:theme";

function getInitialTheme() {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === "dark" || saved === "light") {
    return saved;
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getInitialTab(tools: ToolDefinition[]) {
  const enabledTools = tools.filter((tool) => tool.enabled);
  const hashId = window.location.hash.replace("#", "");
  const savedId = localStorage.getItem(LAST_TAB_KEY);
  return (
    enabledTools.find((tool) => tool.id === hashId)?.id ??
    enabledTools.find((tool) => tool.id === savedId)?.id ??
    enabledTools[0]?.id ??
    ""
  );
}

const ShieldIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const WifiOffIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

function TopBanner() {
  return (
    <div className="top-banner" aria-label="About this tool">
      <div className="container top-banner-inner">
        <div className="top-banner-left">
          <p className="top-banner-headline">Your files never leave your device.</p>
          <p className="top-banner-sub">
            Built because I was tired of uploading documents to sketchy websites.
            Everything runs in your browser — no server, no uploads, no tracking.
            {" "}
            <a className="top-banner-link" href={PROJECT_REPO_URL} target="_blank" rel="noreferrer">Source ↗</a>
          </p>
        </div>
        <div className="top-banner-chips">
          <span className="privacy-chip"><ShieldIcon />Local processing</span>
          <span className="privacy-chip"><LockIcon />No file uploads</span>
          <span className="privacy-chip"><WifiOffIcon />No server calls</span>
          <span className="privacy-chip"><EyeOffIcon />No tracking</span>
        </div>
      </div>
    </div>
  );
}


export function AppShell({ tools = toolRegistry }: AppShellProps) {
  const enabledTools = useMemo(() => tools.filter((tool) => tool.enabled), [tools]);
  const [theme, setTheme] = useState<"light" | "dark">(() => getInitialTheme());
  const [activeId, setActiveId] = useState(() => getInitialTab(enabledTools));
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const activeTool = enabledTools.find((tool) => tool.id === activeId) ?? enabledTools[0];
  const ActiveComponent = useMemo(() => {
    if (!activeTool) {
      return null;
    }
    return lazy(activeTool.load);
  }, [activeTool]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (!activeTool) {
      return;
    }
    localStorage.setItem(LAST_TAB_KEY, activeTool.id);
    if (window.location.hash !== `#${activeTool.id}`) {
      history.replaceState(null, "", `#${activeTool.id}`);
    }
  }, [activeTool]);

  const showToast = (message: string, kind: ToastKind = "success") => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { id, message, kind }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 3600);
  };

  return (
    <div className="app-shell">
      <Header theme={theme} onToggleTheme={() => setTheme((current) => (current === "dark" ? "light" : "dark"))} />
      <TopBanner />
      <main>
        <section className="tool-band" aria-label="Conversion tools">
          <div className="container">
            <Tabs
              tabs={enabledTools.map((tool) => ({ id: tool.id, label: tool.label }))}
              activeId={activeTool?.id ?? ""}
              onChange={setActiveId}
            />
            <div className="tool-frame" key={activeTool?.id}>
              {ActiveComponent ? (
                <Suspense fallback={<div className="loading-panel">Loading local tool...</div>}>
                  <ActiveComponent onToast={showToast} />
                </Suspense>
              ) : (
                <div className="empty-state">No tools are enabled.</div>
              )}
            </div>
          </div>
        </section>
      </main>
      <ToastRegion messages={toasts} />
      <Footer />
    </div>
  );
}

export function App() {
  return <AppShell />;
}
